import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import exportToCSV from "../components/exportToCSV";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export async function loader() {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Response(
      JSON.stringify({
        message: "You must be logged in to access this resource.",
      }),
      { status: 401 }
    );
  }

  const response = await fetch(`${apiUrl}/ct/contacts`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-App-Secret": process.env.REACT_APP_APP_SECRET,
    },
  });

  if (!response.ok) {
    throw new Response(
      JSON.stringify({ message: "Could not fetch contacts." }),
      { status: response.status }
    );
  }

  const contacts = await response.json();
  return contacts;
}

export default function ContactsManagement() {
  const contacts = useLoaderData();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    subject: "",
    emailed: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [editableData, setEditableData] = useState({});

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Filter contacts based on the active filters.
  const filteredContacts = contacts.filter((contact) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === "emailed") {
        // For the emailed filter, compare booleans (or their string equivalents)
        return String(contact.emailed).toLowerCase() === value.toLowerCase();
      }
      return String(contact[key]).toLowerCase().includes(value.toLowerCase());
    })
  );

  const handleEditClick = (id, rowData) => {
    setEditRowId(id);
    // Set the editable field (for now, only "emailed" is editable)
    setEditableData({
      emailed: rowData.emailed ? "true" : "false",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("You must be logged in to update contacts.");
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/ct/contacts/update/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
        // Convert the emailed value to a boolean
        body: JSON.stringify({
          emailed: editableData.emailed === "true",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update contact");
      }

      const updatedContact = await response.json();
      alert(updatedContact.message);

      // Exit edit mode
      setEditRowId(null);
      // full reload to refresh table
      navigate(".", { replace: true });
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("An error occurred while saving changes. Please try again.");
    }
  };

  const handleCancelClick = () => {
    setEditRowId(null);
    setEditableData({});
  };

  return (
    <div className="contacts-management-container">
      <h1>Contacts Management</h1>
      <div className="filters">
        <label>
          Name:
          <input
            type="text"
            name="name"
            value={filters.name}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Email:
          <input
            type="text"
            name="email"
            value={filters.email}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Subject:
          <input
            type="text"
            name="subject"
            value={filters.subject}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Emailed:
          <select
            name="emailed"
            value={filters.emailed}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
      </div>
      <div className="export-button">
        <button
          onClick={() => exportToCSV(filteredContacts, "contacts.csv")}
          className="export-btn"
        >
          Export CSV
        </button>
      </div>
      <table className="management-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Subject</th>
            <th>Message</th>
            <th>Emailed</th>
            <th>Error Message</th>
            <th>Submitted At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredContacts.map((contact) =>
            editRowId === contact.id ? (
              <tr key={contact.id}>
                <td>{contact.id}</td>
                <td>{contact.name}</td>
                <td>{contact.email}</td>
                <td>{contact.subject}</td>
                <td>{contact.message}</td>
                <td>
                  <select
                    name="emailed"
                    value={editableData.emailed}
                    onChange={handleEditChange}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </td>
                <td>{contact.error_message || "-"}</td>
                <td>{new Date(contact.created_at).toLocaleString()}</td>
                <td>
                  <button
                    className="save-btn"
                    onClick={() => handleSaveClick(contact.id)}
                  >
                    Save
                  </button>
                  <button className="cancel-btn" onClick={handleCancelClick}>
                    Cancel
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={contact.id}>
                <td>{contact.id}</td>
                <td>{contact.name}</td>
                <td>{contact.email}</td>
                <td>{contact.subject}</td>
                <td>{contact.message}</td>
                <td>{contact.emailed ? "Yes" : "No"}</td>
                <td>{contact.error_message || "-"}</td>
                <td>{new Date(contact.created_at).toLocaleString()}</td>
                <td>
                  <button
                    className="edit-btn"
                    onClick={() => handleEditClick(contact.id, contact)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
